import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X } from "lucide-react";
import { useSelectTheme } from "@/contexts/SelectThemeContext";

const STORAGE_KEY = "bbk_banner_dismissed";

export default function AnnouncementBanner() {
  const [banner, setBanner] = useState(null);
  const { isSelect } = useSelectTheme();

  useEffect(() => {
    // Re-fetch whenever the active tier changes so Select gets its own scroll.
    // Race guard: if the user switches tier mid-flight (or React resolves the
    // context after mount), the older response must not clobber the newer one.
    // We tag each request with the tier it was made for and only apply the
    // response if it still matches the current tier at completion time.
    let cancelled = false;
    setBanner(null);
    const currentTier = isSelect ? "select" : "supply";
    (async () => {
      try {
        const { data } = await api.get(`/banner/active?tier=${currentTier}`);
        if (cancelled) return;                                // effect re-ran
        // Extra safety: if the tier changed between request start and now
        // (e.g. because a stale response arrived out-of-order), only accept a
        // response whose tier matches what we asked for or is unscoped (both).
        if (data && data.tier && data.tier !== currentTier) return;
        if (!data) return;
        const dismissed = localStorage.getItem(STORAGE_KEY);
        if (dismissed && dismissed === data.id) return;
        setBanner(data);
      } catch {
        /* fine — banner is optional */
      }
    })();
    return () => { cancelled = true; };
  }, [isSelect]);

  if (!banner) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, banner.id);
    setBanner(null);
  };

  const inner = (
    <span className="inline-flex items-center gap-2 whitespace-nowrap">
      <span className="font-medium">{banner.message}</span>
      {banner.link_url && banner.link_label && (
        <span className="underline underline-offset-2 decoration-oxblood/50 hover:decoration-oxblood">
          {banner.link_label} →
        </span>
      )}
    </span>
  );

  if (banner.marquee) {
    const speedClass =
      banner.speed === "glacial" ? "animate-bbk-marquee-glacial"
      : banner.speed === "slow" ? "animate-bbk-marquee-slow"
      : banner.speed === "fast" ? "animate-bbk-marquee-fast"
      : "animate-bbk-marquee";
    return (
      <div
        className={`w-full text-[12px] md:text-[13px] font-medium tracking-wide relative overflow-hidden py-[10px] ${isSelect ? "bg-[#EBE0BE] text-[#1B1614] border-y border-[#DDA94E]/40" : "bg-oxblood text-bone"}`}
        data-testid="announcement-banner"
        data-tier={isSelect ? "select" : "supply"}
      >
        <div
          className={`bbk-marquee hover:[animation-play-state:paused] whitespace-nowrap ${speedClass}`}
          data-testid="announcement-marquee"
          data-speed={banner.speed || "medium"}
          style={{ width: "max-content" }}
        >
          {/* Repeat 8× to guarantee the content is wide enough to cover any
              viewport at any tier font-scale — bumped from 4 to 8 because the
              Select tier's text-scale rules make each copy wider, so 4 copies
              left a visible gap on wide screens. Animation still -50%, so
              half the copies are always off-screen forming the seamless loop. */}
          {[0, 1, 2, 3, 4, 5, 6, 7].map((k) => (
            <span key={k} className="inline-flex items-center gap-8 px-8">
              {banner.link_url ? (
                <a href={banner.link_url} className={isSelect ? "text-[#1B1614] hover:text-[#6E521A]" : "text-bone hover:text-bone/80"} data-testid={k === 0 ? "banner-link" : undefined}>{inner}</a>
              ) : (
                inner
              )}
              <span className={isSelect ? "text-[#6E521A]/60" : "text-bone/85"} aria-hidden>·</span>
            </span>
          ))}
        </div>
        <button
          onClick={dismiss}
          className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors z-10 rounded-full p-1 ${isSelect ? "text-[#1B1614]/70 hover:text-[#6E521A] bg-[#EBE0BE]/80" : "text-bone/70 hover:text-bone bg-oxblood/80"}`}
          aria-label="Dismiss announcement"
          data-testid="banner-dismiss"
        >
          <X size={14} strokeWidth={2}/>
        </button>
      </div>
    );
  }

  return (
    <div
      className={`w-full text-[12px] md:text-[13px] font-medium tracking-wide px-6 py-[10px] flex items-center justify-center gap-4 relative ${isSelect ? "bg-[#EBE0BE] text-[#1B1614] border-y border-[#DDA94E]/40" : "bg-oxblood text-bone"}`}
      data-testid="announcement-banner"
      data-tier={isSelect ? "select" : "supply"}
    >
      {banner.link_url ? (
        <a href={banner.link_url} className={`transition-colors ${isSelect ? "text-[#1B1614] hover:text-[#6E521A]" : "text-bone hover:text-bone/80"}`} data-testid="banner-link">
          {inner}
        </a>
      ) : (
        inner
      )}
      <button
        onClick={dismiss}
        className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isSelect ? "text-[#1B1614]/70 hover:text-[#6E521A]" : "text-bone/70 hover:text-bone"}`}
        aria-label="Dismiss announcement"
        data-testid="banner-dismiss"
      >
        <X size={14} strokeWidth={2}/>
      </button>
    </div>
  );
}
